import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const IMAGES = [
  "/images/succivo.jpg",
  "/images/orta-di-atella.jpg",
  "/images/villa-santa-lucia.webp",
  "https://images.unsplash.com/photo-1523217582562-09d0def993a6?auto=format&fit=crop&w=1200&q=80",
];

export const Structures = () => {
  const { t } = useLanguage();

  return (
    <section id="strutture" data-testid="structures-section" className="py-24 lg:py-32 bg-creamdark border-t border-line">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-xs md:text-sm font-semibold uppercase tracking-[0.22em] text-terra mb-6"
        >
          {t.structures.eyebrow}
        </motion.p>
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-6 font-serif font-medium tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            {t.structures.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5 lg:col-start-8 text-base md:text-lg leading-relaxed text-inksoft self-end"
          >
            {t.structures.sub}
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
          {t.structures.items.map((s, i) => (
            <motion.article
              key={i}
              data-testid={`structure-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className={`group ${i % 2 === 1 ? "md:mt-14" : ""}`}
            >
              <div className="relative overflow-hidden rounded-3xl aspect-[4/3] border border-line shadow-[0_20px_60px_rgba(45,50,43,0.12)]">
                <img
                  src={IMAGES[i]}
                  alt={`Struttura residenziale — ${s.name} (${s.province})`}
                  loading="lazy"
                  className="w-full h-full object-cover scale-100 group-hover:scale-105 transition-transform duration-700 ease-out"
                  data-testid={`structure-image-${i}`}
                />
                <span className="absolute top-5 right-5 bg-cream/90 backdrop-blur text-ink text-xs font-semibold uppercase tracking-widest px-3.5 py-1.5 rounded-full">
                  {s.region} · {s.province}
                </span>
              </div>
              <div className="mt-5 px-1">
                <div className="flex items-end justify-between">
                  <h3 className="font-serif text-3xl lg:text-4xl font-medium text-ink">{s.name}</h3>
                  <span className="font-serif italic text-xl text-inksoft/50">0{i + 1}</span>
                </div>
                <div className="mt-2.5 flex items-center gap-2">
                  <MapPin size={16} strokeWidth={1.5} className="text-terra shrink-0" />
                  <p className="text-sm font-semibold text-ink">{s.address}</p>
                </div>
                <p className="mt-2 text-sm md:text-base leading-relaxed text-inksoft">{s.text}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
